import React, { useState, useEffect } from 'react';
import { documentsAPI, casesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import {
  Plus, Search, Download, Trash2, FileText, Upload, Wand2,
  ChevronDown, ChevronRight, Briefcase, FolderOpen, Folder
} from 'lucide-react';
import { toast } from 'react-toastify';

const DOC_TYPES = [
  'Pleading', 'Contract', 'Evidence', 'Court Order', 'Summons',
  'Heads of Arguments', 'Bail Application', 'Affidavit', 'Notice of Motion',
  'Founding Affidavit', 'Title Deed', 'Receipt', 'Invoice',
  'Retainer Agreement', 'Power of Attorney', 'Other',
];

const TEMPLATES = [
  'Summons', 'Heads of Arguments', 'Bail Application', 'Affidavit',
  'Notice of Motion', 'Founding Affidavit', 'Retainer Agreement',
];

const TYPE_COLOUR = {
  'Pleading': 'bg-blue-50 text-blue-700',
  'Contract': 'bg-green-50 text-green-700',
  'Evidence': 'bg-yellow-50 text-yellow-700',
  'Court Order': 'bg-red-50 text-red-700',
  'Affidavit': 'bg-purple-50 text-purple-700',
  'Bail Application': 'bg-orange-50 text-orange-700',
  'Title Deed': 'bg-teal-50 text-teal-700',
};

const typeColour = (t) => TYPE_COLOUR[t] || 'bg-gray-100 text-gray-600';

// ── grouped by case ──────────────────────────────────────────────────────────
function groupByCase(docs) {
  const groups = {};
  docs.forEach(d => {
    const key = d.case_title || '__none__';
    if (!groups[key]) groups[key] = [];
    groups[key].push(d);
  });
  // Sort: real cases first (alphabetically), then "No Case"
  return Object.entries(groups).sort(([a], [b]) => {
    if (a === '__none__') return 1;
    if (b === '__none__') return -1;
    return a.localeCompare(b);
  });
}

export default function Documents() {
  const [docs, setDocs]                 = useState([]);
  const [cases, setCases]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [caseFilter, setCaseFilter]     = useState('');
  const [openGroups, setOpenGroups]     = useState(new Set());
  const [uploadModal, setUploadModal]   = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [uploadForm, setUploadForm]     = useState({
    case: '', document_name: '', document_type: 'Other', description: '', version: '1.0',
  });
  const [file, setFile]   = useState(null);
  const [genForm, setGenForm] = useState({ template_type: 'Summons', case_id: '', format: 'docx' });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)     params.search        = search;
      if (typeFilter) params.document_type = typeFilter;
      if (caseFilter) params.case          = caseFilter;
      const { data } = await documentsAPI.list(params);
      const allDocs  = data.results || data;
      setDocs(allDocs);
      // Open all groups by default
      const keys = new Set(allDocs.map(d => d.case_title || '__none__'));
      setOpenGroups(keys);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    casesAPI.list({ page_size: 200 }).then(r => setCases(r.data.results || r.data));
  }, []);

  useEffect(() => { load(); }, [search, typeFilter, caseFilter]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(uploadForm).forEach(([k, v]) => v && fd.append(k, v));
      fd.append('file', file);
      await documentsAPI.upload(fd);
      toast.success('Document uploaded');
      setUploadModal(false);
      setUploadForm({ case: '', document_name: '', document_type: 'Other', description: '', version: '1.0' });
      setFile(null);
      load();
    } catch { toast.error('Upload failed'); }
    finally { setSaving(false); }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await documentsAPI.generate(genForm);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${genForm.template_type.replace(/ /g, '_')}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${genForm.template_type} generated`);
      setGenerateModal(false);
    } catch { toast.error('Failed to generate document'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await documentsAPI.delete(id);
    toast.success('Deleted');
    load();
  };

  const groups = groupByCase(docs);
  const totalDocs = docs.length;

  return (
    <div className="p-6">
      <PageHeader
        title="Documents"
        subtitle={`${totalDocs} document${totalDocs !== 1 ? 's' : ''} across ${groups.length} matter${groups.length !== 1 ? 's' : ''}`}
        actions={
          <div className="flex gap-2">
            <button className="btn-secondary flex items-center gap-2" onClick={() => setGenerateModal(true)}>
              <Wand2 size={15} /> Generate Template
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={() => setUploadModal(true)}>
              <Upload size={15} /> Upload
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="card mb-5 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search documents..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input sm:w-52" value={caseFilter} onChange={e => setCaseFilter(e.target.value)}>
          <option value="">All Matters</option>
          {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
        </select>
        <select className="input sm:w-48" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : docs.length === 0 ? (
        <div className="card text-center py-16">
          <FileText size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No documents found</p>
          <p className="text-gray-400 text-sm mt-1">Upload a document or generate a legal template to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(([caseKey, caseDocs]) => {
            const isOpen   = openGroups.has(caseKey);
            const isNone   = caseKey === '__none__';
            const label    = isNone ? 'No Case Assigned' : caseKey;

            return (
              <div key={caseKey} className="card p-0 overflow-hidden">
                {/* Case header — click to expand/collapse */}
                <button
                  className="w-full flex items-center gap-3 px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left border-b border-gray-100"
                  onClick={() => toggleGroup(caseKey)}
                >
                  {isOpen
                    ? <FolderOpen size={18} className={isNone ? 'text-gray-400' : 'text-blue-600'} />
                    : <Folder     size={18} className={isNone ? 'text-gray-400' : 'text-blue-600'} />
                  }
                  <span className={`font-semibold text-sm flex-1 ${isNone ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                    {label}
                  </span>
                  <span className="text-xs text-gray-400 bg-white border border-gray-200 rounded-full px-2 py-0.5 mr-2">
                    {caseDocs.length} doc{caseDocs.length !== 1 ? 's' : ''}
                  </span>
                  {isOpen
                    ? <ChevronDown  size={15} className="text-gray-400 flex-shrink-0" />
                    : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />
                  }
                </button>

                {/* Document rows */}
                {isOpen && (
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-gray-100">
                      <tr>
                        {['Document', 'Type', 'Version', 'Size', 'Date Uploaded', 'Uploaded By', ''].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {caseDocs.map(d => (
                        <tr key={d.id} className="hover:bg-blue-50/30 group">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <FileText size={16} className="text-blue-500 flex-shrink-0" />
                              <span className="font-medium text-gray-800 text-sm">{d.document_name}</span>
                            </div>
                            {d.description && (
                              <p className="text-xs text-gray-400 mt-0.5 ml-7 truncate max-w-xs">{d.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColour(d.document_type)}`}>
                              {d.document_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">v{d.version}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{d.file_size_kb} KB</td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {new Date(d.created_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">{d.uploaded_by_name || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a
                                href={`http://localhost:8000${d.file}`}
                                target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 rounded px-2 py-1"
                              >
                                <Download size={11} /> Download
                              </a>
                              <button
                                className="p-1.5 text-gray-300 hover:text-red-500 rounded border border-gray-200"
                                onClick={() => handleDelete(d.id)}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      <Modal isOpen={uploadModal} onClose={() => setUploadModal(false)} title="Upload Document">
        <form onSubmit={handleUpload} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Document Name *</label>
            <input className="input" required value={uploadForm.document_name}
              onChange={e => setUploadForm(f => ({ ...f, document_name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Document Type</label>
            <select className="input" value={uploadForm.document_type}
              onChange={e => setUploadForm(f => ({ ...f, document_type: e.target.value }))}>
              {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Linked Case</label>
            <select className="input" value={uploadForm.case}
              onChange={e => setUploadForm(f => ({ ...f, case: e.target.value }))}>
              <option value="">None</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Version</label>
            <input className="input" value={uploadForm.version}
              onChange={e => setUploadForm(f => ({ ...f, version: e.target.value }))} />
          </div>
          <div>
            <label className="label">File *</label>
            <input type="file" className="input" required onChange={e => setFile(e.target.files[0])} />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea className="input" rows={2} value={uploadForm.description}
              onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setUploadModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Uploading...' : 'Upload'}</button>
          </div>
        </form>
      </Modal>

      {/* Generate Template Modal */}
      <Modal isOpen={generateModal} onClose={() => setGenerateModal(false)} title="Generate Legal Document">
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="label">Template Type *</label>
            <select className="input" value={genForm.template_type}
              onChange={e => setGenForm(f => ({ ...f, template_type: e.target.value }))}>
              {TEMPLATES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Case *</label>
            <select className="input" required value={genForm.case_id}
              onChange={e => setGenForm(f => ({ ...f, case_id: e.target.value }))}>
              <option value="">Select case</option>
              {cases.map(c => <option key={c.id} value={c.id}>{c.case_title} — {c.client_name}</option>)}
            </select>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
            <p className="font-medium mb-1">Auto-populated from case data:</p>
            <ul className="list-disc list-inside text-xs space-y-0.5 text-blue-600">
              <li>Firm name, address and contact details</li>
              <li>Client name and ID number</li>
              <li>Case number, type and description</li>
              <li>Attorney name, court name and opposing party</li>
            </ul>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setGenerateModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              <Wand2 size={15} />{saving ? 'Generating...' : 'Generate DOCX'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
